import { Button } from "@/components/ui/baseUi/button"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import {
  errorToastClassName,
  sonnerToast,
  successToastClassName,
  appToast as toast,
} from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { DurationType } from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { useSubscriberPauseFormik } from "@modules/programs/programSubscriber/formik/useSubscriberPauseFormik"
import { useProgramSubscriberActions } from "@modules/programs/programSubscriber/hooks/useProgramSubscriber"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { ChevronLeft, LoaderCircle } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

type ProgramSubscriberPauseFormProps = {
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  moveToSubscriberDetails: () => void
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscriberPauseForm({
  subscriberDetails,
  moveToSubscriberDetails,
  programId,
  className,
  ...props
}: ProgramSubscriberPauseFormProps) {
  const language = useLanguage()
  const [isDisableMonths, setIsDisableMonths] = useState(false)
  const [isResumeVisible, setIsResumeVisible] = useState(false)
  const { resumeSubscription, isResumeSubscriptionLoading } = useProgramSubscriberActions(programId, subscriberDetails)
  const { formik, isSubmitting } = useSubscriberPauseFormik(programId, subscriberDetails, moveToSubscriberDetails)

  const suspensionTypesOptions = useMemo(() => {
    return [
      { value: DurationType.MONTHLY, label: word("box.member.table.offer.pause.monthlyInputLabel") },
      { value: DurationType.UNLIMITED, label: word("box.member.table.offer.pause.unlimitedInputLabel") },
    ]
  }, [language])

  const handleOnResume = () => {
    return Promise.resolve(
      void (() => {
        sonnerToast.promise(
          resumeSubscription()
            .then(() => {
              toast.success(word("program.subscribers.subscriptionDetails.resume.success"), {
                classNames: successToastClassName(),
              })
              setIsResumeVisible(false)
              moveToSubscriberDetails()
            })
            .catch((e: any) => {
              console.error(LoggerType.Service + "resume subscription", e)
              toast.error(word("box.member.table.offer.pause.toastResumeError"), {
                classNames: errorToastClassName(),
              })
            }),
        )
      }),
    )
  }

  useEffect(() => {
    if (subscriberDetails) {
      const { durationType, months } = subscriberDetails.getPause()
      formik.setFieldValue("durationType", durationType)
      formik.setFieldValue("months", months)

      if (subscriberDetails.pause) setIsResumeVisible(true)
    }
  }, [subscriberDetails])

  const durationText = useMemo(() => {
    if (subscriberDetails.pause?.resumeAt) {
      return {
        title: word("box.member.table.offer.resume.defineDuration", {
          count: dayjs(subscriberDetails.pause.resumeAt)
            .add(1, "day")
            .diff(dayjs(subscriberDetails.pause.pausedAt), "months"),
        }),
        subtitle: `${dayjs(subscriberDetails.pause.pausedAt).format("ll")} ${word("global.periodInterval.to")}${" "}${dayjs(
          subscriberDetails.pause.resumeAt,
        ).format("ll")}`,
      }
    } else if (subscriberDetails.pause) {
      return {
        title: word("box.member.table.offer.resume.unlimitedDuration"),
        subtitle: `  ${word("box.member.table.offer.resume.unlimitedDurationSince")} ${dayjs(
          subscriberDetails.pause.pausedAt,
        ).format("LL")}`,
      }
    }
    return { title: "", subtitle: "" }
  }, [subscriberDetails])

  return (
    <div className={cn("flex grow flex-col gap-5 p-2 pt-1", className)} {...props}>
      {/* Form header */}
      <div className="flex flex-col items-center gap-3">
        <div className="relative flex w-full flex-row justify-center">
          <Button variant="underline" onClick={moveToSubscriberDetails} className="absolute left-0">
            <ChevronLeft color="currentColor" size={13} className="text-textDefault-caption" />
            {word("global.return")}
          </Button>

          <Text fontStyle="BL" className="text-center">
            {word("program.subscribers.subscriptionDetails.pause.title")}
          </Text>
        </div>

        <Text fontStyle="BM" className="text-center text-textDefault-caption">
          {subscriberDetails instanceof ProgramBundleSubscriberEntity
            ? word("programBundle.subscribers.subscriptionDetails.pause.description")
            : word("program.subscribers.subscriptionDetails.pause.description")}
        </Text>
      </div>

      {/* Content */}
      {isResumeVisible ? (
        // RESUME
        <div className="flex grow flex-col gap-2">
          <div className="flex grow flex-col gap-2">
            <Text fontStyle="LM" className="text-textDefault-body">
              {word("box.member.table.offer.pause.supensionTypeLabel")}
            </Text>
            <Text fontStyle="TM" className="text-textDefault-heading">
              {durationText.title}
            </Text>
            <Text fontStyle="LM" className="mt-2 text-textDefault-body">
              {durationText.subtitle}
            </Text>
          </div>

          <div className="mt-auto flex flex-row items-center gap-6">
            <Button
              variant="primary"
              onClick={(e) => {
                e.preventDefault()
                handleOnResume()
              }}
              className="w-full"
              disabled={isResumeSubscriptionLoading}
              isLoading={isResumeSubscriptionLoading}
            >
              {isResumeSubscriptionLoading && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
              {word("box.member.table.offer.pause.resumeButton")}
            </Button>
          </div>
        </div>
      ) : (
        // PAUSE FORM
        <div className="flex grow flex-col gap-2">
          <div className="flex grow flex-col gap-2">
            <DropdownInput
              label={word("box.member.table.offer.pause.supensionTypeLabel")}
              options={suspensionTypesOptions}
              isRequiredInput={true}
              error={getFormErrorMessage("durationType", formik)}
            >
              <Select
                defaultValue={formik.values.durationType}
                onValueChange={(value) => {
                  formik.setFieldValue("durationType", value)
                  if (value === DurationType.UNLIMITED) {
                    setIsDisableMonths(true)
                    formik.setFieldValue("months", null)
                  } else {
                    formik.setFieldValue("months", 1)
                    setIsDisableMonths(false)
                  }
                }}
                items={suspensionTypesOptions}
              >
                <SelectTrigger id={"durationType"}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {suspensionTypesOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DropdownInput>

            <StandardInput
              isRequiredInput
              getFieldProps={formik.getFieldProps}
              name="months"
              error={getFormErrorMessage("months", formik)}
              setFieldValue={formik.setFieldValue}
              inputProps={{
                type: "number",
                inputMode: "numeric",
                step: 1,
                min: 0,
                disabled: isDisableMonths,
                onChange: (e) => {
                  formik.setFieldValue("months", parseInt(e.target.value))
                },
              }}
            >
              <Text fontStyle="TS" className="ml-4 flex items-center justify-center text-center text-textDefault-body">
                {word("global.month.label", { count: formik.values.months ?? 1 })}
              </Text>
            </StandardInput>
          </div>

          {/* Submit button */}
          <div className="mt-auto flex flex-row items-center gap-6">
            <Button variant="secondary" className="flex-1" onClick={moveToSubscriberDetails} disabled={isSubmitting}>
              {word("global.cancel")}
            </Button>

            <Button
              isLoading={isSubmitting}
              variant="primary"
              className="flex-1"
              onClick={formik.submitForm}
              disabled={isSubmitting}
            >
              {isSubmitting && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
              {word("global.save")}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
